import { Fragment, useState } from 'react';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material';

// components
import { TableCircularProgress } from 'src/components/table';
import CasTableCell from './_internal/CasTableCell';
import ICasTableColumn from './ICasTableColumn';
import CasTableError from './CasTableError';
import CasTableNoData from './CasTableNoData';

export type TRowSx = (row: any) => SxProps<Theme>;

interface ICasTableBody {
  loading: boolean;
  columns: ICasTableColumn[];
  rowData: any[];
  error: null | string;
  rowSx?: TRowSx;
  rowChildrenColumns?: ICasTableColumn[];
  rowChildrenSx?: TRowSx;
}

export default function CasTableBody({
  loading = false,
  columns,
  rowData,
  error,
  rowSx,
  rowChildrenColumns,
  rowChildrenSx,
}: ICasTableBody) {
  // Keyed by the row's id where it has one, not its position: a paged grid reuses positions, so an
  // index key left whatever row landed there on the next page opened.
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const expandKey = (row: any, index: number) => String(row?.id ?? `#${index}`);

  return (
    <TableBody>
      {loading ? (
        <TableCircularProgress loading={loading} />
      ) : (
        rowData?.map((row, index) => (
          // Fragment, not <>, because the key has to sit on the element this map RETURNS - a row
          // is followed by its expanded child rows, so the pair is wrapped. The shorthand cannot
          // take a key, which is how it went missing when expandable rows were folded in here.
          <Fragment key={index}>
            <TableRow sx={{ ...(rowSx ? rowSx(row) : {}) }} hover>
              {columns?.map((column, colIndex) => (
                <CasTableCell
                  key={`${index}${colIndex}`}
                  row={row}
                  column={column}
                  onToggleExpand={() =>
                    setExpandedRows((prev) => ({
                      ...prev,
                      [expandKey(row, index)]: !prev[expandKey(row, index)],
                    }))
                  }
                  isExpanded={expandedRows[expandKey(row, index)]}
                />
              ))}
            </TableRow>

            {expandedRows[expandKey(row, index)] &&
              row?.subMenus?.map((childRow: any, childIndex: number) => (
                <TableRow
                  key={`child-${index}-${childIndex}`}
                  sx={{ ...(rowChildrenSx ? rowChildrenSx(childRow) : {}) }}
                >
                  {rowChildrenColumns?.map((childColumn: ICasTableColumn, colIndex: number) => (
                    <CasTableCell
                      key={`child-cell-${index}-${childIndex}-${colIndex}`}
                      row={childRow}
                      column={childColumn}
                    />
                  ))}
                </TableRow>
              ))}
          </Fragment>
        ))
      )}
      {!rowData?.length && !loading && !error && <CasTableNoData notFound />}
      {error && <CasTableError message={error} />}
    </TableBody>
  );
}
